/**
 * « Bien démarrer » présenté comme une académie : des thèmes (cours) faits de
 * leçons courtes, chacune avec sa progression.
 *
 * Une leçon peut être :
 * - une visite guidée seule (apprendre un geste de l'interface) ;
 * - une étape du parcours du parc, apprise avec sa visite quand elle existe ;
 * - un tuto (thème « Aller plus loin ») : sa vidéo et sa leçon pas à pas, qui
 *   ne font qu'une carte.
 *
 * Une leçon est terminée quand sa visite a été suivie jusqu'au bout OU quand
 * l'étape est faite dans le parc (la personne n'a plus rien à apprendre pour
 * avancer). Une étape confiée à l'administration ou mise de côté ne compte pas
 * dans la progression : elle reste visible, mais n'attend rien de la personne.
 */

import { resolveProductFeatures } from "../../config/productFeatureCatalog";
import { GUIDED_TOUR_BY_ID } from "../../navigation-v2/tour/tourCatalogue";
import { ONBOARDING_STEPS } from "./onboardingJourney";
import { EXPLORE_VIDEO_BY_ID, LESSON_VIDEO_BY_ID, videoForVariant } from "./onboardingVideos";

export const ACADEMY_COURSE_IDS = Object.freeze(["interface", "park", "collection", "welfare", "team", "explore"]);

const SECONDS_PER_SCENE = 20;

const lesson = (id, source) => Object.freeze({ id, ...source });

export const ACADEMY_COURSES = Object.freeze([
  Object.freeze({ id: "interface", lessons: Object.freeze([
    lesson("interfaceDiscover", { tourId: "interfaceDiscover" }),
  ]) }),
  Object.freeze({ id: "park", lessons: Object.freeze([
    lesson("sectors", { stepId: "sectors" }),
    lesson("enclosures", { stepId: "enclosures" }),
    lesson("zims", { stepId: "zims" }),
  ]) }),
  Object.freeze({ id: "collection", lessons: Object.freeze([
    lesson("individuals", { stepId: "individuals" }),
    lesson("individualsImportZims", { tourId: "individualsImportZims" }),
    lesson("groups", { stepId: "groups" }),
    lesson("groupMembers", { stepId: "groupMembers" }),
  ]) }),
  Object.freeze({ id: "welfare", lessons: Object.freeze([
    lesson("fastEvaluation", { stepId: "fastEvaluation" }),
    lesson("results", { stepId: "results" }),
    lesson("objectives", { stepId: "objectives" }),
  ]) }),
  // Les comptes et les mots de passe ne concernent personne le premier jour :
  // ils arrivent avec l'équipe, quand on invite et qu'on donne les bons rôles.
  // Les tâches ne sont plus ici : elles relèvent du cahier de secteur, dont elles
  // sont une leçon complémentaire (retour du 27/10/2026).
  Object.freeze({ id: "team", lessons: Object.freeze([
    lesson("team", { stepId: "team" }),
    lesson("teamAccounts", { tourId: "usersManageAccount" }),
    lesson("accountPassword", { tourId: "accountPassword" }),
  ]) }),
  // Un tuto par module, dans l'ordre de la série vidéo : sa vidéo, puis sa leçon
  // pas à pas, qui suit les gestes montrés dans la vidéo.
  Object.freeze({ id: "explore", lessons: Object.freeze([
    // Les tâches sont un sujet du cahier de secteur (programme Cahier de secteur) :
    // leçon complémentaire de son tuto, et non une leçon de « Travailler en équipe ».
    lesson("sectorLogbook", { stepId: "dailyReports", tourId: "sectorLogbookDiscover", videoId: "sectorLogbook", moreTourIds: ["tasksCreate"] }),
    lesson("dailyMonitoring", { tourId: "dailyMonitoringDiscover", videoId: "dailyMonitoring" }),
    lesson("parameters", { tourId: "parametersCreate", videoId: "parameters" }),
    lesson("multiParameterAnalysis", { tourId: "multiParameterAnalysisExplore", videoId: "multiParameterAnalysis" }),
    // Le tuto vétérinaire porte aussi, dans le même bloc, la leçon « Une
    // ordonnance pour plusieurs animaux » (sans vidéo), la pharmacie, sous-thème
    // qui a son propre film (`moreVideoIds`, décision du 27/10/2026), et les
    // stupéfiants (registre, 04/11/2026, sans film pour l'instant).
    lesson("veterinary", {
      tourId: "veterinaryPrescription",
      videoId: "veterinary",
      moreTourIds: ["veterinaryMultiAnimalPrescription", "pharmacyDiscover", "pharmacyControlledDiscover"],
      moreVideoIds: Object.freeze({ pharmacyDiscover: "pharmacy" }),
    }),
    lesson("enrichments", { stepId: "enrichments", videoId: "enrichments" }),
    lesson("training", { tourId: "trainingExerciseCreate", videoId: "training" }),
    lesson("scans", { tourId: "scanCampaignCreate", videoId: "scans" }),
    lesson("focals", { tourId: "focalCampaignCreate", videoId: "focals" }),
    lesson("questionnaires", { tourId: "questionnaireCampaignCreate", videoId: "questionnaires" }),
    lesson("rafiki", { tourId: "rafikiAsk", videoId: "rafiki" }),
    lesson("researchProjects", { tourId: "researchProjectsDiscover", videoId: "researchProjects" }),
  ]) }),
]);

/**
 * Les offres d'un parc, dans l'ordre de la gamme : les trois offres
 * d'évaluation et le programme Cahier de secteur, qui se prend seul ou en plus.
 * Chaque leçon dit dans lesquelles elle est incluse (retour du 26/10/2026 : un
 * parc Discovery voyait des thèmes à moitié vides, sans savoir pourquoi).
 */
export const ACADEMY_OFFER_IDS = Object.freeze(["discovery", "essential", "progress", "logbook"]);

const OFFER_SCOPES = Object.freeze(Object.fromEntries(ACADEMY_OFFER_IDS.map((id) => [id, id === "logbook"
  ? resolveProductFeatures({ programs: ["logbook"] })
  : resolveProductFeatures({ tier: id })])));

const STEP_BY_ID = new Map(ONBOARDING_STEPS.map((item) => [item.id, item]));

/**
 * Ce qu'une leçon demande à l'offre : les fonctions de son étape, sinon (ou en
 * plus) celles de sa visite. Il suffit qu'une des deux soit ouverte, comme
 * dans `buildOnboardingAcademy`.
 */
const lessonRequirements = (entry) => {
  const step = entry.stepId ? STEP_BY_ID.get(entry.stepId) || null : null;
  const tour = GUIDED_TOUR_BY_ID[entry.tourId || step?.tourId] || null;
  return [
    step ? { anyOf: step.features, allOf: step.allFeatures } : null,
    tour ? { anyOf: tour.features, allOf: [] } : null,
  ].filter(Boolean);
};

const meets = (requirements, can) => requirements.some(({ anyOf, allOf }) => (!anyOf.length || anyOf.some(can)) && allOf.every(can));

/**
 * Les offres qui incluent la leçon. Une fonction qu'un type d'établissement
 * n'a jamais (ZIMS pour un laboratoire, `scope.excludes`) n'est dans aucune.
 */
export const lessonOffers = (entry, scope = null) => {
  const requirements = lessonRequirements(entry);
  const excluded = typeof scope?.excludes === "function" ? scope.excludes : () => false;
  return Object.freeze(ACADEMY_OFFER_IDS.filter((id) => meets(requirements, (feature) => !excluded(feature) && OFFER_SCOPES[id].can(feature))));
};

/** L'offre du parc et son programme Cahier de secteur, parmi `ACADEMY_OFFER_IDS`. */
export const parkOfferIds = (scope) => {
  const tier = scope?.tier || scope?.legacyTier || null;
  return Object.freeze(ACADEMY_OFFER_IDS.filter((id) => (id === "logbook" ? (scope?.programs || []).includes("logbook") : id === tier)));
};

const offersOf = (lessons) => Object.freeze(ACADEMY_OFFER_IDS.filter((id) => lessons.some((item) => item.offers.includes(id))));

/** Durée indicative d'une visite : une vingtaine de secondes par scène. */
export const tourMinutes = (sceneCount) => Math.max(1, Math.round((sceneCount * SECONDS_PER_SCENE) / 60));

const progressOf = (lessons) => {
  const counted = lessons.filter((item) => item.counted);
  const done = counted.filter((item) => item.status === "done").length;
  return Object.freeze({ done, total: counted.length, ratio: counted.length ? done / counted.length : 1 });
};

/**
 * @param {object} input
 * @param {object} input.journey          résultat de buildOnboardingJourney
 * @param {Array}  input.tours            visites proposées à la personne (catalogue filtré)
 * @param {object} [input.completedTours] { [tourId]: horodatage }
 */
export function buildOnboardingAcademy({ journey, tours = [], completedTours = {}, scope = null }) {
  const tourById = new Map(tours.map((item) => [item.id, item]));
  const stepById = new Map(journey.steps.map((item) => [item.id, item]));
  // Sans offre connue (scope absent ou parc sans composition), rien n'est verrouillé.
  const parkCan = scope && scope.mode !== "legacy" && typeof scope.can === "function" ? scope.can : null;

  const courses = ACADEMY_COURSES.map((definition) => {
    const lessons = definition.lessons.map((entry) => {
      const offers = lessonOffers(entry, scope);
      const video = entry.videoId ? videoForVariant(EXPLORE_VIDEO_BY_ID[entry.videoId] || null) : null;
      const step = entry.stepId ? stepById.get(entry.stepId) || null : null;
      const tourId = entry.tourId || step?.tourId || null;
      const tour = tourId ? tourById.get(tourId) || null : null;
      const available = entry.stepId ? Boolean(step) : Boolean(tour);
      if (!available) {
        // Hors de l'offre du parc mais dans une autre : la leçon reste visible,
        // verrouillée ; sa vidéo se regarde, sa visite ne se lance pas. Une
        // leçon que l'offre permet mais que les droits de la personne ferment
        // reste cachée, comme avant.
        if (!parkCan || !offers.length || meets(lessonRequirements(entry), parkCan)) return null;
        return Object.freeze({
          id: entry.id,
          courseId: definition.id,
          status: "locked",
          counted: false,
          locked: true,
          learned: false,
          achieved: false,
          step: null,
          tour: null,
          moreTours: Object.freeze([]),
          copy: Object.freeze({ stepId: entry.stepId || null, tourId }),
          offers,
          video,
        });
      }
      const learned = Boolean(tour && completedTours[tour.id]);
      const achieved = step?.status === "done";
      const status = achieved || learned ? "done"
        : step?.status === "skipped" ? "skipped"
          : step?.status === "delegated" ? "delegated"
            : step?.status === "blocked" ? "blocked"
              : "ready";
      return Object.freeze({
        id: entry.id,
        courseId: definition.id,
        status,
        counted: !["skipped", "delegated"].includes(status),
        locked: false,
        learned,
        achieved,
        step,
        tour: tour ? Object.freeze({ id: tour.id, scenes: tour.scenes.length, minutes: tourMinutes(tour.scenes.length) }) : null,
        // Autres leçons pas à pas du même bloc : proposées, sans compter dans la progression.
        // Un sous-thème peut avoir son film (`moreVideoIds`), montré sur sa carte.
        moreTours: Object.freeze((entry.moreTourIds || []).map((id) => tourById.get(id)).filter(Boolean).map((item) => Object.freeze({
          id: item.id,
          scenes: item.scenes.length,
          minutes: tourMinutes(item.scenes.length),
          learned: Boolean(completedTours[item.id]),
          film: videoForVariant(LESSON_VIDEO_BY_ID[entry.moreVideoIds?.[item.id]] || null),
        }))),
        copy: Object.freeze({ stepId: entry.stepId || null, tourId }),
        offers,
        video,
      });
    }).filter(Boolean);
    const progress = progressOf(lessons);
    const minutes = lessons.reduce((sum, item) => sum + (item.tour?.minutes || 0), 0);
    return Object.freeze({
      id: definition.id,
      lessons: Object.freeze(lessons),
      progress,
      minutes,
      offers: offersOf(lessons),
      // Thème entièrement hors de l'offre du parc : sa vidéo seulement.
      locked: lessons.length > 0 && lessons.every((item) => item.locked),
      status: progress.total && progress.done === progress.total ? "done" : progress.done ? "started" : "new",
    });
  }).filter((item) => item.lessons.length > 0);

  const allLessons = courses.flatMap((item) => item.lessons);
  const progress = progressOf(allLessons);

  // Reprendre : l'étape que le parcours du parc recommande si elle attend encore
  // une leçon, sinon la première leçon non terminée dans l'ordre des thèmes.
  const recommended = journey.nextStep
    ? allLessons.find((item) => item.step?.id === journey.nextStep.id && item.status !== "done")
    : null;
  const resume = recommended
    || allLessons.find((item) => item.counted && item.status !== "done" && item.status !== "blocked")
    || allLessons.find((item) => item.counted && item.status !== "done")
    || null;

  return Object.freeze({
    courses: Object.freeze(courses),
    parkOffers: parkOfferIds(scope),
    progress,
    resume,
    complete: progress.total > 0 && progress.done === progress.total,
  });
}

/**
 * Lien direct vers une leçon ou un thème de « Bien démarrer » :
 * `/v2/getting-started?lesson=<leçon ou visite>` lance la visite guidée de la
 * leçon ; `?course=<thème>` ouvre le thème. La leçon se désigne par son id
 * d'académie (« veterinary ») ou par l'id de sa visite
 * (« veterinaryMultiAnimalPrescription »), y compris une visite rattachée au
 * bloc d'une autre leçon. Une leçon sans visite ouvre son thème.
 * Rien n'est lancé pour une leçon que la personne ne peut pas suivre (offre,
 * droits) : la page s'ouvre normalement. `lessonId` désigne la leçon (pour un
 * tuto d'« Aller plus loin », la page du tuto à ouvrir).
 * @returns {{ tourId: ?string, courseId: ?string, lessonId?: string } | null}
 */
export function resolveLessonLink(academy, search) {
  if (!academy) return null;
  const params = new URLSearchParams(search || "");
  const lessonParam = params.get("lesson");
  const courseParam = params.get("course");
  if (lessonParam) {
    for (const course of academy.courses) {
      for (const item of course.lessons) {
        if (item.moreTours?.some((more) => more.id === lessonParam)) return { tourId: lessonParam, courseId: course.id, lessonId: item.id };
        if (item.id === lessonParam || item.tour?.id === lessonParam) {
          return { tourId: item.tour?.id || null, courseId: course.id, lessonId: item.id };
        }
      }
    }
  }
  if (courseParam && academy.courses.some((course) => course.id === courseParam)) return { tourId: null, courseId: courseParam };
  return null;
}
